import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, IDR, DATE, formatErr } from "@/lib/api";
import DataTable, { EmptyState } from "@/components/app/DataTable";
import { canApprove, canEditProduction, useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function EnvCosts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category_id: "", amount: 0, billing_mode: "hpp", description: "", batch_id: "", product_id: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [es, cs, ps, bs] = await Promise.all([
        api.get("/env-costs"), api.get("/env-cost-categories"), api.get("/products"), api.get("/batches"),
      ]);
      setRows(es.data); setCats(cs.data); setProducts(ps.data); setBatches(bs.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ category_id: cats[0]?.id || "", amount: 0, billing_mode: cats[0]?.default_billing_mode || "hpp", description: "", batch_id: "", product_id: "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const cat = cats.find((c) => c.id === form.category_id);
      await api.post("/env-costs", { ...form, category_name: cat?.name, amount: Number(form.amount || 0) });
      toast.success("Biaya lingkungan dicatat"); setOpen(false); load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const verify = async (r) => {
    try { await api.post(`/env-costs/${r.id}/verify`); toast.success("Terverifikasi"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Hapus entri biaya lingkungan?")) return;
    try { await api.delete(`/env-costs/${r.id}`); toast.success("Entri dihapus"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="gc-label">Green Accounting</span>
          <h1 className="gc-h1 mt-1">Biaya Lingkungan</h1>
          <p className="mt-2 text-slate-500 max-w-2xl">Rekam biaya lingkungan yang teridentifikasi. Pastikan mode pembebanan tepat untuk menghindari <em>double counting</em>.</p>
        </div>
        {canEditProduction(user) && user?.active_role !== "operator" && (
          <button onClick={openNew} className="gc-btn-primary" data-testid="btn-new-envcost"><Plus className="w-4 h-4" /> Catat Biaya</button>
        )}
      </div>

      {loading ? <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div> : (
        <DataTable
          rows={rows}
          columns={[
            { key: "entry_date", header: "Tanggal", render: (r) => DATE(r.entry_date) },
            { key: "category_name", header: "Kategori" },
            { key: "billing_mode", header: "Mode", render: (r) => (<span className={`gc-badge ${r.billing_mode === "hpp" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-700"}`}>{r.billing_mode === "hpp" ? "HPP" : "Pelaporan"}</span>) },
            { key: "amount", header: "Nilai", align: "right", render: (r) => <span className="font-semibold">{IDR(r.amount)}</span> },
            { key: "description", header: "Deskripsi" },
            { key: "is_verified", header: "Verifikasi", render: (r) => r.is_verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-slate-400">Belum</span> },
            { key: "actions", header: "Aksi", render: (r) => (
              <div className="flex items-center gap-1 justify-end">
                {!r.is_verified && canApprove(user) && <button onClick={(e) => { e.stopPropagation(); verify(r); }} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-700" title="Verifikasi"><CheckCircle2 className="w-4 h-4" /></button>}
                {canEditProduction(user) && <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
            )},
          ]}
          empty={<EmptyState title="Belum ada entri" desc="Catat biaya energi, air, pengelolaan limbah, dan biaya lingkungan lainnya." />}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-cabinet">Catat Biaya Lingkungan</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="gc-label block mb-1.5">Kategori *</label>
              <select required className="gc-input" value={form.category_id} onChange={(e) => {
                const cat = cats.find((c) => c.id === e.target.value);
                setForm({ ...form, category_id: e.target.value, billing_mode: cat?.default_billing_mode || "hpp" });
              }} data-testid="env-category">
                <option value="">— Pilih —</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="gc-label block mb-1.5">Mode Pembebanan *</label>
              <select className="gc-input" value={form.billing_mode} onChange={(e) => setForm({ ...form, billing_mode: e.target.value })} data-testid="env-mode">
                <option value="hpp">Dibebankan ke HPP</option>
                <option value="report_only">Hanya Pelaporan</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="gc-label block mb-1.5">Produk (opsional)</label>
                <select className="gc-input" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                  <option value="">—</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="gc-label block mb-1.5">Batch (opsional)</label>
                <select className="gc-input" value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })}>
                  <option value="">—</option>
                  {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_no}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="gc-label block mb-1.5">Nilai (IDR) *</label>
              <input required type="number" className="gc-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="env-amount" />
            </div>
            <div>
              <label className="gc-label block mb-1.5">Deskripsi</label>
              <textarea className="gc-textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={() => setOpen(false)} className="gc-btn-ghost">Batal</button>
              <button type="submit" disabled={saving} className="gc-btn-primary" data-testid="env-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
